import { Component, inject, Input, NgZone, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ClubService } from "../../services/club.service";
import { debounceTime, distinctUntilChanged, Subject, switchMap, tap } from "rxjs";
import { SearchDonateToModel } from "../../models/pickup-request.model";
import { GooglePlacesService } from "../../services/google-places.service";
import { AddressModel } from "../../models/address.model";
import PlacesService = google.maps.places.PlacesService;
import { UserService } from "../../services/user.service";
import { TranslateService } from "@ngx-translate/core";
import { CountryRestrictionService } from "../../services/country-restriction.service";
import { CountryISO, SearchCountryField } from 'ngx-intl-tel-input';
import { PhoneNumberUtil } from 'google-libphonenumber';

@Component({
  selector: 'app-team-profile',
  templateUrl: './team-profile.component.html',
  styleUrl: './team-profile.component.scss'
})
export class TeamProfileComponent implements OnInit  {
  @Input() userData!: any;

  private _fb: FormBuilder = inject(FormBuilder);
  private _clubService: ClubService = inject(ClubService);
  private _googlePlacesService: GooglePlacesService = inject(GooglePlacesService);
  private _ngZone: NgZone = inject(NgZone);
  private _userService: UserService = inject(UserService);
  private _translateService: TranslateService = inject(TranslateService);
  private _countryRestrictionService: CountryRestrictionService = inject(CountryRestrictionService);
  private _platformId: Object = inject(PLATFORM_ID);
  private _phoneUtil = PhoneNumberUtil.getInstance();

  profileForm!: FormGroup;
  securityForm!: FormGroup;

  editProfileMode: boolean = false;
  editSecurityMode: boolean = false;

  loadingProfile: boolean = false;
  loadingSecurity: boolean = false;
  loadingForm: boolean = false;

  studentCouncil!: boolean;
  formError: any;
  error: boolean = false;
  errorMessage!: string | null;

  showPassword: boolean = false;
  showConfirmPassword: boolean = false;

  clubOptions!: any[];
  loadingClubs!: boolean;
  clubSearch$: Subject<string> = new Subject<string>();
  selectedClub!: any;

  addressSearch$: Subject<string> = new Subject<string>();
  selectedAddress!: any;
  loadingAddress!: boolean;
  addressSuggestions: AddressModel[] = [];

  profileInitialValue!: any;
  securityInitialValue!: any;

  isBrowser: boolean = isPlatformBrowser(this._platformId);
  preferredCountries: CountryISO[] = [CountryISO.Norway];
  onlyCountries: CountryISO[] = [];
  selectedCountryISO: CountryISO = CountryISO.Norway;
  readonly searchCountryFields: SearchCountryField[] = [SearchCountryField.Iso2, SearchCountryField.Name];

  ngOnInit(): void {
    this.studentCouncil = this.userData.profile.school;

    this.setupPhoneCountryOptions();
    this.initForms();

    this.clubSearch$
      .pipe(
        tap((): void => {
          this.loadingClubs = true;
        }),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(query => this._clubService.searchClub(query, this.userData.profile.sport_type))
      )
      .subscribe({
        next: (result: {query: string, results: SearchDonateToModel[]}) => {
          this.loadingClubs = false;
          this.clubOptions = result.results;
        },
        error: (): void => {
          this.loadingClubs = false;
        }
      });

    this.addressSearch$
      .pipe(
        tap(() => this.loadingAddress = true),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(query => this._googlePlacesService.getPlacePredictions(query))
      )
      .subscribe({
        next: (results: google.maps.places.AutocompletePrediction[]) => {
          this.loadingAddress = false;
          this.addressSuggestions = results.map((prediction): any => ({
            placeId: prediction.place_id,
            displayName: prediction.description
          }));
        },
        error: () => this.loadingAddress = false
      });
  }

  initForms(): void {
    const ageGroupValue = this.formatAgeGroupValue(this.userData.profile.age_group);

    this.profileForm = this._fb.group({
      club: [this.userData.profile.club.name, Validators.required],
      teamName: [this.userData.profile.name, Validators.required],
      address: [this.userData.profile.address_line, Validators.required],
      league: [this.userData.profile.league, this.studentCouncil ? [] : Validators.required],
      ageGroup: [ageGroupValue, Validators.required],
      numberOfPlayers: [this.userData.profile.number_of_players, [Validators.required, Validators.min(2)]],
      gender: [this.userData.profile.gender, this.studentCouncil ? [] : Validators.required],
      managerName: [this.userData.profile.manager_name, Validators.required],
      contactPersonName: [this.userData.profile.contact_person_name, Validators.required],
      managerAddress: [this.userData.profile.manager_address, Validators.required],
      phone: [this.getInitialPhoneControlValue(), Validators.required],
      email: [this.userData.email, [Validators.required, Validators.email]],
    });

    this.securityForm = this._fb.group({
      password: ['************', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  toggleEdit(profile: boolean, security: boolean): void {
    if (profile) {
      this.editProfileMode = !this.editProfileMode;
      this.profileInitialValue = JSON.parse(JSON.stringify(this.profileForm.value));
    }
    if (security) {
      this.editSecurityMode = !this.editSecurityMode;
      this.securityInitialValue = JSON.parse(JSON.stringify(this.securityForm.value));
      if (this.editSecurityMode) this.securityForm.controls['password'].setValue('');
    }
  }

  cancelEdit(profile: boolean, security: boolean): void {
    if (profile) {
      this.editProfileMode = false;
      if (this.profileInitialValue) {
        this.profileForm.setValue(this.profileInitialValue);
        this.profileForm.markAsPristine();
        this.profileForm.markAsUntouched();
        this.profileForm.updateValueAndValidity();
      }
    }
    if (security) {
      this.editSecurityMode = false;
      if (this.securityInitialValue) {
        this.securityForm.setValue(this.securityInitialValue);
        this.securityForm.markAsPristine();
        this.securityForm.markAsUntouched();
        this.securityForm.updateValueAndValidity();
      }
    }
  }

  saveChanges(profile: boolean, security: boolean): void {
    if (profile && this.profileForm.invalid) return;
    if (security && this.securityForm.invalid) return;

    this.formError = null;
    this.error = false;

    if (profile) {
      this.loadingProfile = true;

      const formData = this.profileForm.value;
      const normalizedPhone: string = this.extractE164Phone(formData.phone);
      if (!normalizedPhone) {
        this.loadingProfile = false;
        const phoneControl = this.profileForm.get('phone');
        phoneControl?.setErrors({ validatePhoneNumber: true });
        phoneControl?.markAsTouched();
        return;
      }

      const phoneCountryCode: string = (this.selectedCountryISO || CountryISO.Norway).toUpperCase();

      const { start, end } = this.parseAgeGroup(formData.ageGroup);

      let associatedClub: any = {};
      if (this.selectedClub) {
        associatedClub.id = this.selectedClub.id;
      } else {
        associatedClub.name = formData.club;
      }

      let updateData: any = {
        email: formData.email as string,
        name: formData.teamName as string,
        manager_name: formData.managerName as string,
        manager_address: formData.managerAddress as string,
        contact_person_name: formData.contactPersonName as string,
        phone_number: normalizedPhone,
        league: formData.league as string,
        age_group: [start, end],
        number_of_players: formData.numberOfPlayers,
        sport_type: formData.sportType as string,
        gender: formData.gender as string,
        country_code: phoneCountryCode
      }

      if (associatedClub && Object.keys(associatedClub).length > 0) {
        updateData = {
          ...updateData,
          associated_club: associatedClub
        }
      }

      if (this.selectedAddress) {
        updateData = {
          ...updateData,
          address_line: formData.address as string,
          postal_code: this.selectedAddress.address.postcode || '',
          city: this.selectedAddress.address.city || this.selectedAddress.address.town || '',
          state: this.selectedAddress.address.state || '',
          country: this.selectedAddress.address.country_code.toUpperCase(),
        }
      }


      // TODO request for update user profile
      this._userService.updateTeam(updateData).subscribe(() => {
        this.editProfileMode = false;
        this.loadingProfile = false;
      }, error => {
        this.loadingProfile = false;

        if (error.url.includes('/auth/token/refresh/')) {
          this._userService.delete();
        }
        const details = error.error.detail; // TODO
        this.loadingForm = false;

        const jsonReadyStr: string = `{${details.replace(/^(\w+):/, '"$1":').replace(/'/g, '"')}}`;
        const errorObj = JSON.parse(jsonReadyStr);

        if (error && error.status === 400) {
          this.formError = errorObj;
        } else {
          this.errorMessage = details || this._translateService.instant('PROFILE.ERRORS.GENERIC');
          this.error = true;
        }
      });
    }


    if (security) {
      this.loadingSecurity = true;

      // TODO request for update user password

      this.editSecurityMode = false;
      this.loadingSecurity = false;
    }
  }

  togglePasswordVisibility(field: 'password' | 'confirmPassword') {
    if (field === 'password') {
      this.showPassword = !this.showPassword;
    } else if (field === 'confirmPassword') {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }

  onClubSearch(event: Event): void {
    this.loadingClubs = true;
    const input: HTMLInputElement = event.target as HTMLInputElement;
    const query: string = input?.value || '';

    if (query.length > 1) {
      this.clubSearch$.next(query);
    } else {
      this.loadingClubs = false;
    }
  }

  selectClubOption(option: any): void {
    this.selectedClub = option;

    this.profileForm.patchValue({
      club: option.name
    });
    this.clubOptions = []; // Clear options after selection
  }

  onAddressSearch(event: Event): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    const query: string = input?.value.trim() || '';

    if (query.length > 1) {
      this.addressSearch$.next(query);
    } else {
      this.addressSuggestions = [];
      this.loadingAddress = false;
    }
  }

  async selectAddress(address: AddressModel): Promise<void> {
    try {
      const placesService: PlacesService = new google.maps.places.PlacesService(document.createElement('div'));

      placesService.getDetails({ placeId: address.placeId }, (place: any, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          this._ngZone.run(() => {
            this.selectedAddress = {
              display_name: address.displayName,
              address: {
                postcode: place.address_components?.find((c: any) => c.types.includes("postal_code"))?.long_name || '',
                city: place.address_components?.find((c: any) => c.types.includes("locality"))?.long_name ||
                  place.address_components?.find((c: any) => c.types.includes("administrative_area_level_2"))?.long_name || '',
                state: place.address_components?.find((c: any) => c.types.includes("administrative_area_level_1"))?.long_name || '',
                country_code: place.address_components?.find((c: any) => c.types.includes("country"))?.short_name || '',
              },
              lon: place.geometry?.location?.lng().toString() || '',
              lat: place.geometry?.location?.lat().toString() || ''
            };

            this.profileForm.patchValue({ address: address.displayName });
            this.addressSuggestions = [];
          });
        }
      });

    } catch (error) {
      console.error("Error fetching place details:", error);
    }
  }

  objectKeys(obj: object): string[] {
    return Object.keys(obj);
  }

  getPhoneDisplayValue(): string {
    const value = this.profileForm?.get('phone')?.value;
    if (!value) {
      return '';
    }

    if (typeof value === 'string') {
      const hasPlus = value.startsWith('+');
      const countryIso = this.getProfileCountryISO() || this.selectedCountryISO || CountryISO.Norway;
      const dialCode = this.getDialCodeForCountry(countryIso);

      if (!hasPlus) {
        return `${dialCode} ${value}`.trim();
      }

      const parsed = this.parsePhoneParts(value, countryIso);
      return `${parsed.dialCode} ${parsed.nationalNumber}`.trim();
    }

    const dialCode = value.dialCode || '';
    const number = value.number || value.nationalNumber || '';
    if (dialCode || number) {
      return `${dialCode} ${number}`.trim();
    }

    return value.internationalNumber || value.e164Number || '';
  }

  onPhoneCountryChange(country: CountryISO | { iso2: string } | null | undefined): void {
    const iso = typeof country === 'string'
      ? country
      : country?.iso2;

    if (!iso) {
      return;
    }

    this.selectedCountryISO = iso.toLowerCase() as CountryISO;
  }

  private setupPhoneCountryOptions(): void {
    const allowedCountries = this._countryRestrictionService.getAllowedCountries();
    const mappedCountries = this.mapToCountryIso(allowedCountries);
    const allCountries = this.getAllCountryIsoValues();

    this.onlyCountries = this.uniqueCountries(mappedCountries.length > 1 ? mappedCountries : allCountries);

    const profileCountryIso = this.getProfileCountryISO();
    const defaultCountry = this.onlyCountries.some(code => code === CountryISO.Norway)
      ? CountryISO.Norway
      : (this.onlyCountries[0] || CountryISO.Norway);

    this.selectedCountryISO = profileCountryIso || defaultCountry;

    const preferred = this.uniqueCountries([CountryISO.Norway, ...mappedCountries]);
    this.preferredCountries = this.onlyCountries.length > 1 ? preferred : [];
  }

  private mapToCountryIso(codes: string[]): CountryISO[] {
    const allCountryIsoValues = this.getAllCountryIsoValues();
    const validCountries = new Set<CountryISO>(allCountryIsoValues);

    return codes
      .map(code => (code || '').toLowerCase() as CountryISO)
      .filter(code => validCountries.has(code));
  }

  private uniqueCountries(countries: CountryISO[]): CountryISO[] {
    return Array.from(new Set(countries));
  }

  private getAllCountryIsoValues(): CountryISO[] {
    return Object.keys(CountryISO)
      .map(key => CountryISO[key as keyof typeof CountryISO])
      .filter((value): value is CountryISO => typeof value === 'string');
  }

  private getProfileCountryISO(): CountryISO | null {
    const profileCountryCode = (this.userData?.profile?.country_code || this.userData?.country_code || '').trim();
    if (profileCountryCode && profileCountryCode.length === 2) {
      return profileCountryCode.toLowerCase() as CountryISO;
    }

    return null;
  }

  private getInitialPhoneControlValue(): any {
    const phone = this.userData?.profile?.phone_number;
    if (!phone) {
      return undefined;
    }

    const profileIso = this.getProfileCountryISO() || this.selectedCountryISO || CountryISO.Norway;
    const parsed = this.parsePhoneParts(phone, profileIso);
    this.selectedCountryISO = parsed.countryIso;

    // Show only the national part in the field; dial code comes from the dropdown
    return parsed.nationalNumber;
  }

  private extractE164Phone(phoneValue: any): string {
    if (!phoneValue) {
      return '';
    }

    if (typeof phoneValue === 'object') {
      if (phoneValue.countryCode) {
        this.selectedCountryISO = (phoneValue.countryCode as string).toLowerCase() as CountryISO;
      }

      if (phoneValue.e164Number) {
        return phoneValue.e164Number;
      }

      if (phoneValue.internationalNumber) {
        return this.normalizePhoneNumber(phoneValue.internationalNumber);
      }

      const raw = phoneValue.number || phoneValue.nationalNumber;
      if (raw) {
        const dial = this.getDialCodeForCountry(this.selectedCountryISO || CountryISO.Norway);
        return this.normalizePhoneNumber(`${dial}${raw}`);
      }
    }

    if (typeof phoneValue === 'string') {
      if (phoneValue.startsWith('+')) {
        return this.normalizePhoneNumber(phoneValue);
      }

      const dial = this.getDialCodeForCountry(this.selectedCountryISO || CountryISO.Norway);
      return this.normalizePhoneNumber(`${dial}${phoneValue}`);
    }

    return '';
  }

  private normalizePhoneNumber(phone: string): string {
    if (!phone) {
      return '';
    }

    const digitsOnly = phone.replace(/[^0-9+]/g, '').replace(/^\++/, '');
    if (!digitsOnly) {
      return '';
    }

    return `+${digitsOnly}`;
  }

  private parsePhoneParts(phone: string, fallbackIso: CountryISO | null): { nationalNumber: string; dialCode: string; countryIso: CountryISO } {
    const sanitizedPhone = this.normalizePhoneNumber(phone);
    const defaultIso = fallbackIso || CountryISO.Norway;
    const defaultDialCode = this.getDialCodeForCountry(defaultIso);
    const stripped = sanitizedPhone.replace(/^\+/, '');
    const defaultNational = stripped.replace(new RegExp(`^${defaultDialCode.replace('+', '')}`), '');

    try {
      const parsedNumber = this._phoneUtil.parse(sanitizedPhone, defaultIso.toUpperCase());
      const derivedIso = this._phoneUtil.getRegionCodeForNumber(parsedNumber)?.toLowerCase() as CountryISO | undefined;
      return {
        nationalNumber: parsedNumber.getNationalNumber().toString(),
        dialCode: `+${parsedNumber.getCountryCode()}`,
        countryIso: derivedIso || defaultIso
      };
    } catch {
      return {
        nationalNumber: defaultNational || stripped,
        dialCode: defaultDialCode,
        countryIso: defaultIso
      };
    }
  }

  private getDialCodeForCountry(countryIso: CountryISO): string {
    try {
      const code = this._phoneUtil.getCountryCodeForRegion(countryIso.toUpperCase());
      if (code) {
        return `+${code}`;
      }
    } catch {}
    return '+47';
  }

  private formatAgeGroupValue(ageGroup: [number, number]): string {
    if (!Array.isArray(ageGroup) || ageGroup.length !== 2) {
      return '';
    }

    const start = this.leftPad2(ageGroup[0]);
    const end = this.leftPad2(ageGroup[1]);

    return `${start}-${end}`;
  }

  private parseAgeGroup(value: any): { start: number, end: number } {
    const digits = (value || '').toString().replace(/\D/g, '').padEnd(4, '0');
    const start = parseInt(digits.slice(0, 2), 10) || 0;
    const end = parseInt(digits.slice(2, 4), 10) || 0;

    return { start, end };
  }

  private leftPad2(value: number): string {
    const str = (value ?? 0).toString();
    return ('0' + str).slice(-2);
  }
}
